import { useState } from 'react';

import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@@/ui/primitives';
import { FilePlus2Icon, PlusIcon, SaveIcon } from 'lucide-react';

import { Spinner } from '@@/ui/components';
import { useCreateItem } from '../../hooks/use-items';
import { EntityRecord } from '../../api/api-entities';
import { ItemRecord } from '../../api/api-items';
import { toast } from 'sonner';
import { ItemForm } from './item-form';
import { createAttributeValidationSchema } from './validation';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

export function ItemEditorSheet(props: {
  entity: EntityRecord;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues?: ItemRecord;
  trigger?: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { mutateAsync: createItem, isPending } = useCreateItem();

  const isControlled = props.isOpen !== undefined;
  const isOpen = isControlled ? props.isOpen : internalOpen;
  const setOpen = isControlled ? (props.onOpenChange ?? (() => {})) : setInternalOpen;
  const schema = createAttributeValidationSchema(props.entity);
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: props.initialValues
      ? (props.initialValues.attributes as FormValues)
      : Object.keys(props.entity.schema.fields ?? {}).reduce(
          (defaults, key) => {
            const field = props.entity.schema.fields[key];

            // Otherwise use field defaults
            if (field.definition.type === 'boolean') {
              defaults[key] = false;
            } else if (field.definition.type === 'picklist' && field.definition.options.length > 0) {
              defaults[key] = field.definition.multiple ? field.definition.options[0] : field.definition.options[0];
            } else if (field.definition.type === 'prompt') {
              defaults[key] = { content: '<speak>\n\n</speak>', kind: 'SSML' };
            } else if (field.definition.type === 'connect_resource') {
              defaults[key] = null;
            } else if (field.definition.type === 'date_time') {
              defaults[key] = { timeZone: 'UTC', date: '', time: '' };
            } else if (field.definition.type === 'reference') {
              defaults[key] = { ref: { eid: '', iid: '' } };
            } else {
              defaults[key] = '';
            }
            return defaults;
          },
          {} as Record<string, unknown>
        )
  });

  const handleFormSubmit = async (values: FormValues) => {
    try {
      await createItem({
        entityId: props.entity.entityId,
        data: {
          attributes: values
        }
      });
      toast.success('Item created successfully');
      setOpen(false);
      form.reset();
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred while creating the item.');
      }
    }
  };

  const isDuplicating = !!props.initialValues;

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      {props.trigger ? (
        <SheetTrigger asChild>{props.trigger}</SheetTrigger>
      ) : (
        <SheetTrigger asChild>
          <Button
            variant='default'
            size='sm'
            className='text-sm! font-medium! rounded-full px-3!'
            disabled={Object.keys(props.entity.schema.fields ?? {}).length === 0}
          >
            <PlusIcon />
            Item
          </Button>
        </SheetTrigger>
      )}
      <SheetContent className='overflow-auto min-w-md! font-sans duration-150!'>
        <SheetHeader className='bg-slate-100'>
          <SheetTitle className='text-slate-950'>
            <FilePlus2Icon className='size-6 text-slate-600 mb-2' />
            {isDuplicating ? 'Duplicating' : 'Creating an'} item in {props.entity.name}
          </SheetTitle>
        </SheetHeader>
        <FormProvider {...form}>
          <form {...form} onSubmit={form.handleSubmit(handleFormSubmit)}>
            <ItemForm entity={props.entity} form={form} />
            <SheetFooter>
              <div className='flex justify-end gap-2'>
                <SheetClose asChild>
                  <Button type='button' variant='ghost' className='text-sm! font-medium!'>
                    Cancel
                  </Button>
                </SheetClose>
                <Button
                  type='submit'
                  className='text-sm! leading-snug font-medium! px-3!'
                  disabled={isPending || !form.formState.isValid}
                >
                  <SaveIcon />
                  Save
                  {isPending ? <Spinner /> : null}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
}
